import React, { useMemo, useState } from "react";
import { LinkOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Input,
  Popconfirm,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import truncate from "lodash.truncate";
import { useTranslation } from "react-i18next";

import {
  CheckoutVariantStatus,
  CheckoutVariantTarget,
  type CheckoutVariant,
} from "@fitness-app/data-models/entities/CheckoutVariant";
import { type ProductPrice } from "@fitness-app/data-models/entities/Product";
import { getProductLink } from "@fitness-app/utils/src/products/getProductLink";

import ModalForm from "~/components/ModalForm/ModalForm";
import {
  CURRENT_OFFER_LINKS,
  currentOfferFailureLabel,
  CurrentOfferStatus,
} from "~/modules/AppConfig/currentOffer/CurrentOfferStatus";
import { currentOfferAudienceOf, useCurrentOfferPicker } from "~/modules/AppConfig/currentOffer/useCurrentOfferPicker";
import ProductCheckoutVariantForm from "~/modules/Products/Product/ProductDetails/components/ProductCheckoutVariantForm";
import ProductPageConfigurationForm from "~/modules/Products/Product/ProductDetails/components/ProductPageConfigurationForm";
import { useCheckoutVariantPricesStats } from "~/modules/Products/Product/ProductDetails/hooks/useCheckoutVariantPricesStats";
import { useCheckoutVariants } from "~/modules/Products/Product/ProductDetails/hooks/useCheckoutVariants";
import ProductPrices from "~/modules/Products/Product/ProductPrices/ProductPrices";
import { getPriceFormatter } from "~/modules/Products/utils/getPriceFormatter";
import {
  emptyCheckoutVariantFilters,
  filterCheckoutVariants,
  hasActiveCheckoutVariantFilters,
  type CheckoutVariantFilters,
} from "./filterCheckoutVariants";
import { toManualPaymentFormValues } from "./manualPaymentPrices";
import { ProductCheckoutVariantStats } from "./ProductCheckoutVariantStats";

interface ProductCheckoutVariantsProps {
  prices: ProductPrice[];
  productId: string;
}

const priceFormatter = getPriceFormatter("PLN");

/**
 * Zakładka „Ceny" wariantu. Wydzielona z `expandedRowRender`, żeby statystyki cen
 * mogła policzyć hookiem wprost z opłaconych zamówień.
 */
const CheckoutVariantPricesTab = ({
  variant,
  prices,
  productId,
  onPriceOrderUpdate,
}: {
  variant: CheckoutVariant;
  prices: ProductPrice[];
  productId: string;
  onPriceOrderUpdate: (prices: ProductPrice[]) => void;
}) => {
  const pricesStats = useCheckoutVariantPricesStats(variant.id, productId);

  return (
    <ProductPrices
      allowDnd
      showPriceLabel
      prices={prices}
      productId={productId}
      productSlug={variant.slug || ""}
      readonly
      pricesStats={pricesStats}
      allowCopyLink={false}
      onPriceOrderUpdate={onPriceOrderUpdate}
    />
  );
};

export const ProductCheckoutVariants = ({ productId, prices }: ProductCheckoutVariantsProps): React.ReactElement => {
  const {
    data,
    isLoading,
    updateVariant,
    showFormModal,
    setShowFormModal,
    onVariantSubmit,
    setEditedItem,
    showSavingIndicator,
    editedModel,
    duplicateVariant,
  } = useCheckoutVariants(productId);
  const { t } = useTranslation("products");
  const [filters, setFilters] = useState<CheckoutVariantFilters>(emptyCheckoutVariantFilters);
  const visibleVariants = useMemo(() => filterCheckoutVariants(data ?? [], filters), [data, filters]);
  const updateFilters = (patch: Partial<CheckoutVariantFilters>) => setFilters((prev) => ({ ...prev, ...patch }));
  const currentOffer = useCurrentOfferPicker(productId, data);

  return (
    <div>
      {currentOffer.isEnabled &&
        (["new", "renewal"] as const).map((audience) => (
          <div key={audience} className="flex flex-wrap items-start gap-3 pt-4">
            <div className="min-w-0 flex-1">
              <CurrentOfferStatus audience={audience} resolution={currentOffer.resolve(audience)} />
            </div>
            {currentOffer.getPickedVariantId(audience) && currentOffer.canPick && (
              <Popconfirm
                title={`Link ${CURRENT_OFFER_LINKS[audience]} przestanie prowadzić do wybranej promocji. Kontynuować?`}
                onConfirm={() => currentOffer.pick(audience, "")}
              >
                <Button loading={currentOffer.isSaving}>Odepnij promocję</Button>
              </Popconfirm>
            )}
          </div>
        ))}
      <div className="flex flex-wrap items-center gap-3 py-4">
        <Input
          allowClear
          placeholder="Szukaj po slugu lub ID"
          value={filters.search}
          onChange={(e) => updateFilters({ search: e.target.value })}
          style={{ width: 260 }}
        />
        <Select<CheckoutVariantFilters["target"]>
          allowClear
          placeholder="Typ koszyka"
          value={filters.target ?? undefined}
          onChange={(target) => updateFilters({ target: target ?? null })}
          style={{ width: 170 }}
          options={Object.values(CheckoutVariantTarget).map((target) => ({
            value: target,
            label: t(`checkoutVariants.target.${target}`),
          }))}
        />
        <Select<CheckoutVariantFilters["status"]>
          allowClear
          placeholder="Status"
          value={filters.status ?? undefined}
          onChange={(status) => updateFilters({ status: status ?? null })}
          style={{ width: 140 }}
          options={[
            { value: CheckoutVariantStatus.Active, label: "Aktywny" },
            { value: CheckoutVariantStatus.Inactive, label: "Nieaktywny" },
          ]}
        />
        <Select<CheckoutVariantFilters["availability"]>
          allowClear
          placeholder="Dostępność"
          value={filters.availability ?? undefined}
          onChange={(availability) => updateFilters({ availability: availability ?? null })}
          style={{ width: 180 }}
          options={[
            { value: "unlimited", label: "Brak ograniczeń" },
            { value: "scheduled", label: "Z datą końca" },
            { value: "expired", label: "Wygasłe" },
          ]}
        />
        <Checkbox checked={filters.onlyDefault} onChange={(e) => updateFilters({ onlyDefault: e.target.checked })}>
          Tylko domyślne
        </Checkbox>
        {hasActiveCheckoutVariantFilters(filters) && (
          <Button type="link" onClick={() => setFilters(emptyCheckoutVariantFilters)}>
            Wyczyść filtry
          </Button>
        )}
        <Button type="primary" className="ml-auto" onClick={() => setShowFormModal(true)}>
          Dodaj nowy wariant
        </Button>
      </div>

      <Table<CheckoutVariant>
        dataSource={visibleVariants}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: true }}
        pagination={{
          hideOnSinglePage: true,
          showSizeChanger: true,
          showTotal: (total) =>
            total === data?.length ? `Wariantów: ${total}` : `Wariantów: ${total} z ${data?.length ?? 0}`,
        }}
        expandable={{
          expandedRowRender: (rowData) => {
            const includesIds = rowData.prices;
            const downsellPriceIds = rowData.downsellPrices?.map(({ priceId }) => priceId) ?? [];
            const variantPrices = includesIds
              .map((id) => prices.find((price) => price.id === id))
              .filter((price): price is ProductPrice => !!price);
            const variantDownsellPrices = downsellPriceIds
              .map((id) => prices.find((price) => price.id === id))
              .filter((price): price is ProductPrice => !!price);

            return (
              <Tabs defaultActiveKey="prices">
                <Tabs.TabPane tab={t("checkoutVariants.prices")} key="prices">
                  <CheckoutVariantPricesTab
                    variant={rowData}
                    prices={variantPrices}
                    productId={productId}
                    onPriceOrderUpdate={(prices) => {
                      const updatePricesOrder: CheckoutVariant["prices"] = [];

                      prices.forEach((price) => {
                        const foundPrice = rowData.prices.find((p) => p === price.id);
                        if (foundPrice) {
                          updatePricesOrder.push(foundPrice);
                        }
                      });

                      void updateVariant(rowData.id, { prices: updatePricesOrder, target: rowData.target });
                    }}
                  />
                </Tabs.TabPane>
                <Tabs.TabPane
                  tab={t("checkoutVariants.downsellPrices", { defaultValue: "Ceny downsellowe" })}
                  key="downsellPrices"
                >
                  {variantDownsellPrices.length ? (
                    <ProductPrices
                      allowDnd
                      showPriceLabel
                      prices={variantDownsellPrices}
                      productId={productId}
                      productSlug={rowData.slug || ""}
                      readonly
                      allowCopyLink={false}
                      onPriceOrderUpdate={(prices) => {
                        void updateVariant(rowData.id, {
                          downsellPrices: prices.length ? prices.map((price) => ({ priceId: price.id })) : null,
                          target: rowData.target,
                        });
                      }}
                    />
                  ) : (
                    <Typography.Text type="secondary">
                      {t("checkoutVariants.noDownsellPrices", { defaultValue: "Brak przypisanych cen downsellowych." })}
                    </Typography.Text>
                  )}
                </Tabs.TabPane>
                <Tabs.TabPane tab={t("checkoutVariants.pageConfig")} key="pageConfig">
                  <ProductPageConfigurationForm
                    model={rowData.pageConfiguration}
                    variant={rowData}
                    onSubmit={(pageConfig) => {
                      void updateVariant(rowData.id, {
                        pageConfiguration: pageConfig,
                        target: rowData.target,
                        prices: rowData.prices,
                      });
                    }}
                  />
                </Tabs.TabPane>
                <Tabs.TabPane tab={t("checkoutVariants.stats", { defaultValue: "Statystyki" })} key="stats">
                  <ProductCheckoutVariantStats
                    checkoutVariantIds={[rowData.id]}
                    prices={variantPrices}
                    productId={productId}
                  />
                </Tabs.TabPane>
              </Tabs>
            );
          },
          rowExpandable: (row) => (row.prices?.length || 0) > 0 || (row.downsellPrices?.length || 0) > 0,
        }}
      >
        {currentOffer.isEnabled && (
          <Table.Column<CheckoutVariant>
            title={
              <Tooltip title="Koszyki, do których prowadzą stałe linki z maili ActiveCampaign: /aktualna-oferta (nowe klientki) i /aktualna-oferta-przedluzenie (koszyki przedłużenia).">
                W mailach
              </Tooltip>
            }
            key="currentOffer"
            fixed="left"
            align="center"
            render={(_, row) => {
              const audience = currentOfferAudienceOf(row);
              const isPicked = row.id === currentOffer.getPickedVariantId(audience);
              const ineligibility = currentOffer.getIneligibility(row);
              const radio = (
                <Radio
                  checked={isPicked}
                  disabled={!currentOffer.canPick || currentOffer.isSaving || (!!ineligibility && !isPicked)}
                />
              );

              if (ineligibility || isPicked || !currentOffer.canPick) {
                return (
                  <Tooltip title={ineligibility ? currentOfferFailureLabel(ineligibility, audience) : undefined}>
                    {/* A disabled control swallows the hover, so the tooltip hangs on a wrapper. */}
                    <span>{radio}</span>
                  </Tooltip>
                );
              }

              return (
                <Popconfirm
                  title={`Link ${CURRENT_OFFER_LINKS[audience]} zacznie prowadzić do ${row.slug}. Kontynuować?`}
                  onConfirm={() => currentOffer.pick(audience, row.id)}
                >
                  {radio}
                </Popconfirm>
              );
            }}
          />
        )}
        <Table.Column<CheckoutVariant>
          title="ID"
          dataIndex="id"
          key="id"
          render={(id: string) => (
            <Typography.Paragraph
              className="whitespace-nowrap"
              copyable={{ tooltips: [id, t("common:copied")], text: id }}
              style={{ marginBottom: 0 }}
            >
              {truncate(id, { length: 10 })}
            </Typography.Paragraph>
          )}
        />
        <Table.Column<CheckoutVariant>
          title="Slug"
          key="slug"
          dataIndex="slug"
          minWidth={200}
          render={(slug: string) => (
            <Typography.Paragraph
              className="whitespace-nowrap"
              copyable={{ tooltips: [slug, t("common:copied")], text: slug }}
              style={{ marginBottom: 0 }}
            >
              {truncate(slug, { length: 30 })}
            </Typography.Paragraph>
          )}
        />
        <Table.Column<CheckoutVariant>
          title="Ceny"
          key="prices"
          dataIndex="prices"
          render={(prices: CheckoutVariant["prices"]) => prices?.length}
        />
        <Table.Column<CheckoutVariant>
          title="Downselle"
          key="downsellPrices"
          dataIndex="downsellPrices"
          render={(downsellPrices: CheckoutVariant["downsellPrices"]) => downsellPrices?.length ?? 0}
        />
        <Table.Column<CheckoutVariant>
          title="Odsłony"
          key="views"
          dataIndex="views"
          sorter={(a, b) => (a.views ?? 0) - (b.views ?? 0)}
        />
        <Table.Column<CheckoutVariant>
          title="Ilość tranksacji"
          key="purchase"
          dataIndex="purchase"
          sorter={(a, b) => (a.purchase ?? 0) - (b.purchase ?? 0)}
        />
        <Table.Column<CheckoutVariant>
          title="Przychody"
          key="revenue"
          dataIndex="revenue"
          sorter={(a, b) => (a.revenue ?? 0) - (b.revenue ?? 0)}
          render={(revenue) => priceFormatter.format(revenue / 100)}
        />
        <Table.Column
          title="Typ koszyka"
          key="target"
          dataIndex="target"
          render={(target) => t(`checkoutVariants.target.${target}`)}
        />

        <Table.Column<CheckoutVariant>
          title={t("list.columns.link")}
          dataIndex="link"
          key="link"
          render={(_, row) => {
            const link = getProductLink({
              productId,
              domain: import.meta.env.VITE_ECOMMERCE_DOMAIN,
              slug: row.slug,
              isVariant: true,
              isRenewal: row.target === CheckoutVariantTarget.Renewal,
            });
            return (
              <Tooltip title={t("list.copyToClipboard")}>
                <Button
                  type="link"
                  icon={<LinkOutlined style={{ fontSize: 22 }} />}
                  onClick={() => navigator.clipboard.writeText(link)}
                />
              </Tooltip>
            );
          }}
        />

        <Table.Column<CheckoutVariant>
          title={t("list.columns.active")}
          dataIndex="status"
          key="status"
          render={(status: CheckoutVariant["status"], row) => {
            return (
              <Switch
                checked={status === CheckoutVariantStatus.Active}
                onChange={() => {
                  void updateVariant(row.id, {
                    status:
                      status === CheckoutVariantStatus.Active
                        ? CheckoutVariantStatus.Inactive
                        : CheckoutVariantStatus.Active,
                    target: row.target,
                  });
                }}
              />
            );
          }}
        />

        <Table.Column<CheckoutVariant>
          title="Wariant domyślny"
          dataIndex="default"
          key="default"
          render={(isDefault: CheckoutVariant["default"], row) => {
            return (
              <Switch
                checked={isDefault ?? false}
                onChange={() => {
                  void updateVariant(row.id, {
                    default: !isDefault,
                    target: row.target,
                  });
                }}
              />
            );
          }}
        />

        <Table.Column<CheckoutVariant>
          title="Dostępny do"
          dataIndex="activeUntil"
          key="activeUntil"
          render={(activeUntil: CheckoutVariant["activeUntil"]) => {
            if (!activeUntil) {
              return "Brak ograniczeń";
            }

            if (dayjs.unix(activeUntil).isBefore(dayjs())) {
              return <Tag color="red">{dayjs.unix(activeUntil).format("YYYY-MM-DD HH:mm")} Wygasł</Tag>;
            }

            return <Tag color="blue">{dayjs.unix(activeUntil).format("YYYY-MM-DD HH:mm")}</Tag>;
          }}
        />

        <Table.Column<CheckoutVariant>
          title="Nie proponuj wariantu"
          dataIndex="excluded"
          key="excluded"
          render={(excluded: CheckoutVariant["excluded"], row) => {
            if (row.target === CheckoutVariantTarget.New) {
              return "Nie dotyczy";
            }

            return (
              <Switch
                checked={excluded ?? false}
                onChange={() => {
                  void updateVariant(row.id, {
                    excluded: !excluded,
                    target: row.target,
                  });
                }}
              />
            );
          }}
        />

        <Table.Column<CheckoutVariant>
          title={t("list.columns.options")}
          key="action"
          align="left"
          render={(_, row) => (
            <Space>
              <Button type="link" onClick={() => setEditedItem(row)}>
                {t("common:button.edit")}
              </Button>
              <Button type="link" onClick={() => duplicateVariant(row)}>
                {t("common:button.duplicate")}
              </Button>
              <Popconfirm
                overlayStyle={{ maxWidth: 400 }}
                placement="leftTop"
                title={t("checkoutVariants.archiveWarning")}
                onConfirm={() =>
                  void updateVariant(row.id, {
                    status: CheckoutVariantStatus.Archived,
                    target: row.target,
                  })
                }
              >
                <Button type="link" danger>
                  {t("common:button.archive")}
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>
      <ModalForm
        title="Dodaj nowy wariant koszyka"
        open={showFormModal}
        onCancel={() => setShowFormModal(false)}
        loading={showSavingIndicator}
      >
        <ProductCheckoutVariantForm
          variants={data || []}
          model={
            !editedModel
              ? undefined
              : {
                  target: editedModel.target,
                  slug: editedModel.slug,
                  isActive: editedModel.status === CheckoutVariantStatus.Active,
                  prices: editedModel.prices,
                  downsellPrices: editedModel.downsellPrices?.map(({ priceId }) => priceId) ?? [],
                  default: editedModel.default ?? false,
                  activeUntil: editedModel.activeUntil ? dayjs.unix(editedModel.activeUntil) : null,
                  metadata: editedModel.metadata
                    ? {
                        renewalFallback: editedModel.metadata.renewalFallback
                          ? String(editedModel.metadata.renewalFallback)
                          : null,
                        ...toManualPaymentFormValues(editedModel.metadata.allowManualPaymentForPrices),
                      }
                    : {},
                }
          }
          onSubmit={onVariantSubmit}
        />
      </ModalForm>
    </div>
  );
};
